'use client';

import {
  type ColumnDef,
  flexRender,
  type PaginationState,
  type Row as TanstackRow,
  type RowData,
  type RowSelectionState,
  stockFeatures,
  type StockFeatures,
  type Table as TanstackTable,
  useTable,
} from '@tanstack/react-table';
import type { TableSelection } from '@workspace/ui/hooks';
import { Popover, PopoverContent, PopoverTrigger, TruncateWithTooltip } from '@workspace/ui';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import { TablePagination } from '@workspace/ui/components/custom/table-pagination';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';
import { cn } from '@workspace/ui/lib';
import type { Sort, SortItem } from '@workspace/ui/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip';
import { AlertCircle, ArrowDownUp, Expand, File, ListFilter, Minimize2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';

// Stable empty selection so the table state reference is constant when no
// tableSelection is provided.
const EMPTY_ROW_SELECTION: RowSelectionState = {};

/**
 * Represents an active filter badge
 */
export interface ActiveFilter {
  /** Unique key for the filter */
  key: string;
  /** Display label for the filter */
  label: string;
  /** The filter value(s) */
  value: string | string[];
  /** Callback to clear this specific filter */
  onClear: () => void;
}

/**
 * Props for the DataTable component
 * @template TData - The type of data being displayed in the table
 */
interface DataTableProps<TData extends RowData> {
  /** Array of data items to be displayed in the table */
  data: TData[];

  /** Column definitions for desktop view using TanStack Table's ColumnDef type */
  columns: ColumnDef<StockFeatures, TData>[];

  /** Column definitions for mobile view using TanStack Table's ColumnDef type */
  mobileColumns: ColumnDef<StockFeatures, TData>[];

  /** Pagination state */
  pagination: PaginationState;

  /** Function to set pagination state */
  setPagination: (pagination: PaginationState) => void;

  /** Optional React node containing filter components (search, dropdowns, etc.) */
  filtersComponent?: React.ReactNode;

  /** Optional array of column IDs to hide in tablet view */
  hideColumnsForTablet?: string[];

  /** Optional title to display above the table */
  title?: string;

  /** Optional subtitle to display below the title */
  subTitle?: string | React.ReactNode;

  /** Optional flag to show loading state with skeletons */
  isLoading?: boolean;

  /** Optional flag to show/hide the expand/collapse button */
  showExpandButton?: boolean;

  /** Optional label to display for the expand/collapse button */
  expandButtonLabels?: [string, string];

  /** Optional number of items to display per page in normal view (default: 6) */
  defaultPageSize?: number;

  /** Optional number of items to display per page in expanded view (default: 12) */
  expandedPageSize?: number;

  /** Optional flag to show rows-per-page selector in pagination */
  showPageSizeSelector?: boolean;

  /** Optional rows-per-page options */
  pageSizeOptions?: number[];

  /** Optional label for rows-per-page selector */
  pageSizeSelectorLabel?: string;

  /** Optional total number of items for pagination (useful for server-side pagination) */
  totalItems?: number;

  /** Optional React node containing tab components */
  tabs?: React.ReactNode;

  /** Optional function to show details of a row */
  showDetails?: (row: TData) => void;

  /** Optional React node for an alert above the table */
  alertSlot?: React.ReactNode;

  /** Optional function to show checkbox in each row */
  showSelect?: boolean;

  /** Optional title to display when the table is empty */
  emptyTableTitle?: string;

  /** Optional icon to display when the table is empty */
  emptyTableIcon?: React.ReactNode;

  /** Optional description to display when the table is empty */
  emptyTableDescription?: string;

  /** Optional button to display when the table is empty */
  emptyTableButton?: React.ReactNode;

  /** Optional className to add to the table */
  className?: string;

  /** Optional React node for a custom button */
  customButton?: React.ReactNode;

  /** Optional flag to disable the filter button */
  disableFilters?: boolean;

  /** Optional className to add to the table */
  tableClassName?: string;

  /** Optional flag to use compact table variant */
  compact?: boolean;

  /** Optional flag to enable full-screen expand functionality */
  fullScreenExpand?: boolean;

  /** Optional React node for custom export component */
  exportComponent?: React.ReactNode;

  /** Optional controlled expanded state for full-screen expansion */
  isExpanded?: boolean;

  /** Optional callback when expand state changes for full-screen expansion */
  onExpandChange?: (expanded: boolean) => void;

  /** Optional React node for custom search field controlled by parent form */
  searchField?: React.ReactNode;

  /** Optional array of active filters to display as badges */
  activeFilters?: ActiveFilter[];

  /** Optional callback when all filters are cleared */
  onClearAllFilters?: () => void;

  /** Optional callback when apply filters button is clicked */
  onApplyFilters?: () => void;

  /** Optional callback when clear filters button (in popover) is clicked */
  onClearFiltersInPopover?: () => void;

  /** Optional callback when filters popover open state changes */
  onFiltersOpenChange?: (open: boolean) => void;

  /** Optional className to add to the title */
  titleClassName?: string;

  /** Optional array of sort options to display in the sort popover */
  sortComponent?: SortItem[];

  /** Optional currently active sort value */
  activeSort?: Sort | null;

  /** Optional callback when a sort option is selected */
  onSortChange?: (sort: Sort) => void;

  /** Optional callback to determine if a row should display an error state */
  isRowInErrorState?: (row: TData) => boolean;

  /** Optional callback to determine if a row should be non-selectable and visually muted */
  isRowSelectionDisabled?: (row: TData) => boolean;

  /** Optional callback to add classes to each table row */
  getRowClassName?: (row: TData) => string;

  /** Optional callback to add classes to each table cell */
  getRowCellClassName?: (row: TData, cellIndex: number, totalCells: number) => string;

  /** Optional tooltip for rows where selection is disabled; replaces the checkbox with an alert icon */
  rowSelectionDisabledTooltip?: (row: TData) => string | undefined;

  /** When false, disabled selection rows keep normal row opacity (default: true) */
  muteDisabledSelectionRows?: boolean;

  /**
   * Controlled, id-based selection bundle from useTableSelection. Required to
   * enable row selection; the table renders checkboxes only when both this and
   * showSelect are provided.
   */
  tableSelection?: TableSelection<TData>;
}

/**
 * Formats a filter value for display, truncating if it exceeds the maximum length
 * @param value - The filter value (string or array of strings)
 * @param maxLength - Maximum length for the display value (default: 27)
 * @returns An object containing the display value and remaining count
 */
function formatFilterValue(
  value: string | string[],
  maxLength: number = 27
): { displayValue: string; remainingCount: number | null } {
  let displayValue: string;
  let remainingCount: number | null = null;

  // If value is a string that contains commas, treat it as a comma-separated list
  const items = Array.isArray(value)
    ? value
    : value.includes(',')
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : [value];

  const joined = items.join(', ');
  if (joined.length <= maxLength) {
    displayValue = joined;
  } else if (items.length === 1 && items[0]) {
    // One selection but label is long: truncate only — "(N more)" is for extra selections, not overflow text
    const ellipsis = '...';
    const maxPrefix = Math.max(1, maxLength - ellipsis.length);
    displayValue =
      items[0].length <= maxPrefix ? items[0] : `${items[0].slice(0, maxPrefix)}${ellipsis}`;
    remainingCount = null;
  } else {
    let currentLength = 0;
    let itemsShown = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      const itemLength = item.length + (i > 0 ? 2 : 0); // +2 for ', '
      if (currentLength + itemLength <= maxLength) {
        currentLength += itemLength;
        itemsShown++;
      } else {
        break;
      }
    }
    const hiddenItemCount = items.length - itemsShown;

    if (itemsShown === 0 && items[0]) {
      // First value does not fit in budget; show truncated first + count of other selections
      const otherSelections = items.length - 1;
      const suffix = otherSelections > 0 ? `... (${otherSelections} more)` : '';
      const maxPrefix = suffix ? Math.max(8, maxLength - suffix.length) : Math.max(8, maxLength);
      const prefix = items[0].length <= maxPrefix ? items[0] : items[0].slice(0, maxPrefix);
      displayValue = `${prefix}${suffix}`;
      remainingCount = otherSelections;
    } else {
      const suffix = hiddenItemCount > 0 ? `... (${hiddenItemCount} more)` : '';
      const shownItems = items.slice(0, itemsShown).join(', ');
      displayValue = hiddenItemCount > 0 ? `${shownItems}${suffix}` : shownItems;
      remainingCount = hiddenItemCount > 0 ? hiddenItemCount : null;
    }
  }

  return { displayValue, remainingCount };
}

export function DataTable<TData extends RowData>({
  data,
  columns,
  mobileColumns,
  filtersComponent,
  hideColumnsForTablet,
  title,
  subTitle,
  isLoading = false,
  showExpandButton = false,
  expandButtonLabels,
  tabs,
  defaultPageSize = 10,
  expandedPageSize = 12,
  showPageSizeSelector = false,
  pageSizeOptions,
  pageSizeSelectorLabel,
  totalItems,
  pagination,
  setPagination,
  showDetails,
  alertSlot,
  showSelect = false,
  emptyTableTitle,
  emptyTableIcon,
  emptyTableDescription,
  emptyTableButton,
  customButton,
  exportComponent,
  className,
  tableClassName,
  compact = false,
  fullScreenExpand = false,
  isExpanded: controlledIsExpanded,
  onExpandChange,
  disableFilters = false,
  searchField,
  activeFilters = [],
  onClearAllFilters,
  onApplyFilters,
  onClearFiltersInPopover,
  onFiltersOpenChange,
  titleClassName,
  sortComponent,
  activeSort,
  onSortChange,
  isRowInErrorState,
  isRowSelectionDisabled,
  getRowClassName,
  getRowCellClassName,
  rowSelectionDisabledTooltip,
  muteDisabledSelectionRows = true,
  tableSelection,
}: DataTableProps<TData>) {
  if (fullScreenExpand && (!onExpandChange || controlledIsExpanded === undefined)) {
    console.error(
      'DataTable: When fullScreenExpand is true, both isExpanded and onExpandChange must be provided'
    );
  }

  const [isExpanded, setIsExpanded] = useState(false);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Note: fullScreenExpand is handled by the parent component's inline div wrapper
  // The DataTable only handles page size expansion when fullScreenExpand is false

  const rowSelection = tableSelection?.rowSelection ?? EMPTY_ROW_SELECTION;

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    checkViewport();
  }, []);

  const selectColumn = {
    id: 'select',
    header: ({ table }: { table: TanstackTable<StockFeatures, TData> }) => (
      <Checkbox
        id="select"
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }: { row: TanstackRow<StockFeatures, TData> }) => {
      const isDisabled = !row.getCanSelect();
      const disabledTooltip = isDisabled ? rowSelectionDisabledTooltip?.(row.original) : undefined;

      if (isDisabled && disabledTooltip) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-grey-600 dark:text-grey-400 flex items-center justify-center"
                aria-label={disabledTooltip}
                onClick={(e) => e.stopPropagation()}
              >
                <AlertCircle className="h-4 w-4 text-orange-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{disabledTooltip}</p>
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <Checkbox
          id="select"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          disabled={isDisabled}
        />
      );
    },
    size: isMobile ? 10 : 5,
  };

  let tableColumns = isMobile ? mobileColumns : columns;
  if (showSelect && tableSelection) {
    tableColumns = [selectColumn, ...tableColumns];
  }

  const table = useTable({
    features: stockFeatures,
    data: data || [],
    columns: tableColumns,
    defaultColumn: {
      size: 20,
      minSize: 5,
      maxSize: 100,
    },
    state: {
      pagination,
      columnVisibility: isTablet
        ? hideColumnsForTablet?.reduce(
            (acc, column) => {
              acc[column] = false;
              return acc;
            },
            {} as Record<string, boolean>
          )
        : undefined,
      rowSelection,
    },
    getRowId: tableSelection?.getRowId,
    manualPagination: true,
    rowCount: totalItems ?? data.length,
    enableRowSelection: (row) => {
      const isErrorRow = isRowInErrorState?.(row.original) ?? false;
      const isDisabledRow = isRowSelectionDisabled?.(row.original) ?? false;
      return !isErrorRow && !isDisabledRow;
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    onRowSelectionChange: tableSelection?.onRowSelectionChange,
  });

  const getExpandButton = useCallback(
    (smallScreen: boolean) => {
      const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (fullScreenExpand && onExpandChange) {
          // Full screen expand functionality using controlled state
          onExpandChange(!controlledIsExpanded);
        } else {
          // Original expand functionality (page size only)
          setPagination({
            ...pagination,
            pageSize: !isExpanded ? expandedPageSize : defaultPageSize,
            pageIndex: 1,
          });
          setIsExpanded((prev) => !prev);
        }
      };

      const currentExpanded = fullScreenExpand ? (controlledIsExpanded ?? false) : isExpanded;

      return (
        <Button
          variant={smallScreen ? 'secondary' : 'ghost'}
          className={`items-center justify-center ${smallScreen ? 'flex sm:hidden' : 'hidden p-0 sm:flex'}`}
          onClick={handleClick}
        >
          {currentExpanded ? (
            <Minimize2 className="h-5! w-5! stroke-2!" />
          ) : (
            <Expand className="h-5! w-5! stroke-2!" />
          )}
          {expandButtonLabels && (
            <span className="text-sm font-medium text-neutral-200 dark:text-neutral-100">
              {expandButtonLabels[currentExpanded ? 1 : 0]}
            </span>
          )}
        </Button>
      );
    },
    [
      isExpanded,
      pagination,
      setPagination,
      expandedPageSize,
      defaultPageSize,
      fullScreenExpand,
      onExpandChange,
      controlledIsExpanded,
      expandButtonLabels,
    ]
  );

  const throttledResizeHandler = useThrottledCallback(() => {
    setIsMobile(window.innerWidth <= 768);
    setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
  }, 1000);

  useEffect(() => {
    window.addEventListener('resize', throttledResizeHandler);
    return () => {
      window.removeEventListener('resize', throttledResizeHandler);
    };
  }, [throttledResizeHandler]);

  return (
    <div
      className={cn(
        'bg-grey-25 dark:bg-grey-1000 grid w-full grid-cols-1 items-end gap-5 rounded-4xl p-8 shadow-[0px_429px_120px_rgba(42,43,65,0),_0px_17px_38px_rgba(42,43,65,0.05)]',
        compact && 'sm:!gap-0',
        className
      )}
    >
      <div
        className={cn(
          'border-b-grey-300 dark:border-b-grey-800 flex w-full flex-col items-center gap-5 border-b pb-5 align-middle',
          !isLoading && table.getRowModel()?.rows?.length === 0 && 'xl:border-none'
        )}
      >
        {(title || subTitle || customButton || showExpandButton) && (
          <div
            className={cn(
              'flex w-full flex-col items-start justify-between align-middle sm:flex-row sm:items-center',
              (showExpandButton || (!tabs && filtersComponent) || customButton) && 'gap-6'
            )}
          >
            <div className="flex flex-col items-start">
              {title && (
                <TruncateWithTooltip
                  className={cn(
                    'text-base leading-normal font-medium text-neutral-200 sm:text-lg dark:text-neutral-100',
                    titleClassName
                  )}
                >
                  {title}
                </TruncateWithTooltip>
              )}
              {subTitle && <p className="text-grey-800 mt-1 text-sm">{subTitle}</p>}
            </div>
            <div className="flex w-full flex-col-reverse items-center gap-6 sm:w-fit sm:flex-row sm:gap-6">
              {customButton}
              {showExpandButton && getExpandButton(false)}
            </div>
          </div>
        )}

        {tabs && <div className="flex w-full flex-col items-center gap-4 sm:flex-row">{tabs}</div>}

        {(searchField || filtersComponent) && (
          <div className="flex w-full flex-col gap-3">
            <div className="flex w-full flex-col justify-between gap-6 md:flex-row md:gap-4">
              {searchField && <div className="w-full md:flex-grow">{searchField}</div>}
              {exportComponent && exportComponent}

              {filtersComponent && (
                <Popover
                  open={areFiltersOpen}
                  onOpenChange={(open) => {
                    setAreFiltersOpen(open);
                    onFiltersOpenChange?.(open);
                  }}
                >
                  <PopoverTrigger asChild>
                    {
                      <Button
                        disabled={disableFilters}
                        size="sm"
                        variant={'secondary'}
                        className="w-full text-base md:w-[132px] md:min-w-[132px] md:justify-center md:text-sm"
                      >
                        <ListFilter className="h-4! w-4! stroke-2! md:h-4! md:w-4!" />
                        {'Filters'}
                        {activeFilters && activeFilters.length > 0 && (
                          <div className="flex h-4 w-4 min-w-4 items-center justify-center rounded-full bg-black text-xs font-medium leading-none text-white dark:bg-white dark:text-black">
                            {activeFilters.length}
                          </div>
                        )}
                        {/* <ChevronUp
                            className={`h-5! w-5! stroke-2! md:h-4! md:w-4! ${!areFiltersOpen ? 'rotate-180' : ''}`}
                          /> */}
                      </Button>
                    }
                  </PopoverTrigger>
                  <PopoverContent
                    align={isMobile ? 'center' : 'end'}
                    className="grid w-full grid-cols-1 gap-4 shadow-lg md:w-[573px] md:grid-cols-2 [&_[data-slot='form-item']]:min-w-0 [&_[data-slot='form-item']]:lg:max-w-[253px] [&_[data-slot='form-item']>div:first-child]:lg:max-w-[253px]"
                  >
                    {filtersComponent}

                    <div className="col-span-1 mt-4 flex justify-end gap-2 md:col-span-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-[134px]"
                        onClick={(e) => {
                          e.preventDefault();
                          onClearFiltersInPopover?.();
                          setAreFiltersOpen(false);
                        }}
                      >
                        Clear filters
                      </Button>

                      <Button
                        size="sm"
                        variant="primary"
                        className="w-[134px]"
                        onClick={(e) => {
                          e.preventDefault();
                          onApplyFilters?.();
                          setAreFiltersOpen(false);
                        }}
                      >
                        Apply filters
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {sortComponent && sortComponent.length > 0 && (
                <Popover open={isSortOpen} onOpenChange={setIsSortOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="ghost" aria-label="Sort">
                      <ArrowDownUp className="h-5! w-5! stroke-2!" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[200px] p-[22px]">
                    <div className="flex flex-col gap-[22px]">
                      {sortComponent.map((item) => {
                        const isActive =
                          activeSort?.by === item.sort.by && activeSort?.order === item.sort.order;
                        return (
                          <Button
                            key={`${item.sort.by}-${item.sort.order}`}
                            variant="ghost"
                            className={cn(
                              'hover:bg-grey-50 dark:hover:bg-grey-1000 justify-start rounded-2xl px-4 py-[10px] text-sm text-neutral-200 md:text-base dark:text-neutral-100 hover:dark:text-neutral-100',
                              isActive
                                ? 'bg-grey-50 dark:bg-grey-1000 font-semibold'
                                : 'font-normal'
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              onSortChange?.(item.sort);
                              setIsSortOpen(false);
                            }}
                          >
                            {item.label}
                          </Button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {activeFilters && activeFilters.length > 0 && (
              <div className="flex w-full flex-wrap items-center gap-2">
                {activeFilters.map((filter) => {
                  const { displayValue } = formatFilterValue(filter.value);

                  return (
                    <Badge
                      key={filter.key}
                      variant="filled-grey"
                      size="m"
                      iconAfter={
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            filter.onClear();
                          }}
                          className="text-grey-1100 dark:text-neutral-200"
                          aria-label={`Clear ${filter.label} filter`}
                        >
                          <X className="h-3! w-3!" />
                        </Button>
                      }
                      className="cursor-default"
                    >
                      <span className="text-grey-1100 font-normal dark:text-neutral-200">
                        {filter.label}: <span className="font-medium">{displayValue}</span>
                      </span>
                    </Badge>
                  );
                })}

                {onClearAllFilters && activeFilters.length > 1 && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      onClearAllFilters();
                    }}
                    className="dark:text-grey-400 dark:hover:text-grey-200 h-auto px-2 py-1 text-sm font-medium text-neutral-200"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {showExpandButton && getExpandButton(true)}
        {alertSlot && <div className="w-full">{alertSlot}</div>}
      </div>
      <Table className={cn('w-full', tableClassName)} variant={compact ? 'compact' : 'default'}>
        {!isLoading && table.getRowModel()?.rows?.length !== 0 && !isMobile && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, key, index) => (
              <TableRow key={`${key}-${index}`} className="border-none">
                {headerGroup.headers.map((header, index) => {
                  return (
                    <TableHead
                      key={`${header.id}-${index}`}
                      size={header.column.columnDef.size as number}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {!isLoading
            ? table.getCoreRowModel().rows.map((row, index) => {
                const visibleCells = row.getVisibleCells();
                const isErrorRow = isRowInErrorState?.(row.original) ?? false;
                const isDisabledRow = isRowSelectionDisabled?.(row.original) ?? false;

                return (
                  <TableRow
                    key={`${row.id}-${index}`}
                    className={cn(
                      'border-none',
                      isMobile && 'cursor-pointer',
                      isDisabledRow && muteDisabledSelectionRows && 'opacity-50',
                      getRowClassName?.(row.original)
                    )}
                    onClick={(e) => {
                      e.preventDefault();

                      if (isMobile && showDetails) {
                        showDetails?.(row.original);
                      }
                    }}
                  >
                    {visibleCells.map((cell, cellIndex) => {
                      const isFirstCell = cellIndex === 0;
                      const isLastCell = cellIndex === visibleCells.length - 1;
                      const innerWrapperClassName = cn(
                        isErrorRow && 'border-y border-red-500',
                        isErrorRow && isFirstCell && 'rounded-l-2xl border-l sm:pl-2',
                        isErrorRow && isLastCell && 'rounded-r-2xl border-r sm:pr-2'
                      );

                      return (
                        <TableCell
                          key={`${cell.id}-${cellIndex}`}
                          size={cell.column.columnDef?.size as number}
                          className={getRowCellClassName?.(
                            row.original,
                            cellIndex,
                            visibleCells.length
                          )}
                          withInnerWrapper={isErrorRow}
                          innerWrapperClassName={innerWrapperClassName}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            : [...Array(5)].map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`} className="h-[72px] border-none">
                  <TableCell colSpan={table.getVisibleFlatColumns().length} className="p-4">
                    <Skeleton className="bg-grey-100 dark:bg-grey-800 h-10 w-full" />
                  </TableCell>
                </TableRow>
              ))}
          {!isLoading && table.getRowModel()?.rows?.length === 0 && (
            <TableRow>
              <TableCell colSpan={table.getVisibleFlatColumns().length}>
                <div
                  className={cn(
                    'flex w-full flex-col items-center justify-center gap-2',
                    compact ? 'h-[172px]' : 'h-[457px]'
                  )}
                >
                  {emptyTableIcon ? (
                    emptyTableIcon
                  ) : (
                    <File className="text-grey-400 h-20 w-20 p-4" />
                  )}

                  <div className="flex flex-col items-center justify-center gap-2 text-center">
                    <span className="text-lg leading-normal font-medium text-neutral-200 dark:text-neutral-100">
                      {emptyTableTitle ?? 'No results found'}
                    </span>
                    <span className="text-grey-700 mb-3 dark:text-grey-400 max-w-[265px] text-center text-sm leading-4 text-wrap">
                      {emptyTableDescription ??
                        "Sorry, we couldn't find any results matching your filters."}
                    </span>

                    {emptyTableButton ? emptyTableButton : null}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!isLoading &&
        table.getRowModel()?.rows?.length !== 0 &&
        (Math.ceil((totalItems ?? table.getRowCount()) / pagination.pageSize) > 1 ||
          showPageSizeSelector) && (
          <TablePagination
            className="border-t-grey-300 dark:border-t-grey-800 border-t pt-5"
            totalItems={totalItems ?? table.getRowCount()}
            currentPage={pagination.pageIndex}
            itemsPerPage={pagination.pageSize}
            onPageChange={(page) => setPagination({ ...pagination, pageIndex: page })}
            showPageSizeSelector={showPageSizeSelector}
            pageSizeOptions={pageSizeOptions}
            pageSizeSelectorLabel={pageSizeSelectorLabel}
            onPageSizeChange={(pageSize) =>
              setPagination({ ...pagination, pageIndex: 1, pageSize })
            }
          />
        )}
    </div>
  );
}
